/**
 * Share utilities for encoding/decoding parameters and captions into URL
 */

/**
 * Convert string to base64, supporting UTF-8 characters
 */
function stringToBase64(str) {
  try {
    // Use TextEncoder for UTF-8 support
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const binString = String.fromCharCode(...data);
    return btoa(binString);
  } catch (error) {
    console.error('Failed to encode to base64:', error);
    return '';
  }
}

/**
 * Convert base64 to string, supporting UTF-8 characters
 */
function base64ToString(str) {
  try {
    const binString = atob(str);
    const bytes = Uint8Array.from(binString, (m) => m.charCodeAt(0));
    const decoder = new TextDecoder();
    return decoder.decode(bytes);
  } catch (error) {
    console.error('Failed to decode from base64:', error);
    return '';
  }
}

/**
 * Encode parameters and caption into a compressed URL-safe string
 * @param {Object} params - The parameters object
 * @param {string} caption - Short caption/signature
 * @returns {string} URL-safe encoded string
 */
export function encodeShare(params, caption = '') {
  const data = {
    p: params, // parameters
    c: caption, // caption
  };
  
  // Convert to JSON and compress using base64
  const jsonString = JSON.stringify(data);
  const compressed = stringToBase64(jsonString);
  
  // Make it URL-safe by replacing characters
  return compressed
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
}

/**
 * Decode URL-safe string back to parameters and caption
 * @param {string} encoded - URL-safe encoded string
 * @returns {Object} { params, caption } or null if invalid
 */
export function decodeShare(encoded) {
  try {
    // Restore base64 padding and characters
    let base64 = encoded
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(encoded.length + (4 - (encoded.length % 4)) % 4, '=');
    
    const jsonString = base64ToString(base64);
    const data = JSON.parse(jsonString);
    
    return {
      params: data.p || {},
      caption: data.c || '',
    };
  } catch (error) {
    console.error('Failed to decode share link:', error);
    return null;
  }
}

/**
 * Generate full share URL
 * @param {Object} params - The parameters object
 * @param {string} caption - Short caption/signature
 * @returns {string} Full share URL
 */
export function generateShareUrl(params, caption = '') {
  const encoded = encodeShare(params, caption);
  const baseUrl = window.location.origin + window.location.pathname;
  return `${baseUrl}?share=${encoded}`;
}

/**
 * Get share data from URL search params
 * @returns {Object} { params, caption } or null if no share data
 */
export function getShareFromUrl() {
  const searchParams = new URLSearchParams(window.location.search);
  const shareParam = searchParams.get('share');
  
  if (!shareParam) return null;
  
  return decodeShare(shareParam);
}
